import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Card, CardContent } from "../ui/card";
import { Button } from "../ui/button";
import {
  Banknote, Receipt, Package, ChefHat, Wallet, Percent,
  CheckCircle2, AlertTriangle, CircleDashed, ArrowRight, RefreshCw, Moon,
} from "lucide-react";
import api from "../../lib/api";
import BentoSlotSkeleton from "./BentoSlotSkeleton";

// ── Visual tokens per status ───────────────────────────────────────────
const STATUS_TONE = {
  ok:      { bg: "bg-emerald-50",  bd: "border-emerald-200", fg: "text-emerald-700", Icon: CheckCircle2 },
  warning: { bg: "bg-amber-50",    bd: "border-amber-200",   fg: "text-amber-700",   Icon: AlertTriangle },
  pending: { bg: "bg-orange-50",   bd: "border-orange-200",  fg: "text-orange-700",  Icon: CircleDashed },
  missing: { bg: "bg-rose-50",     bd: "border-rose-200",    fg: "text-rose-700",    Icon: AlertTriangle },
  none:    { bg: "bg-muted/50",     bd: "border-border",    fg: "text-muted-foreground",    Icon: CircleDashed },
};

const ITEM_ICON = {
  cash:        Banknote,
  sales:       Receipt,
  inventory:   Package,
  production:  ChefHat,
  expenses:    Wallet,
  food_cost:   Percent,
};

// ── Route mapping for each CTA action ──────────────────────────────────
const ACTION_ROUTES = {
  open_cash:        "/app/finance?tab=closings",
  close_cash:       "/app/finance?tab=closings",
  view_cash:        "/app/finance?tab=closings",
  close_sales_day:  "/app/finance?tab=closings",
  view_sales:       "/app/finance?tab=closings",
  close_inventory:  "/app/inventory",
  view_inventory:   "/app/inventory",
  close_production: "/app/finance?tab=closings",
  view_production:  "/app/finance?tab=closings",
  review_inbox:     "/app/finance?tab=inbox",
  view_inbox:       "/app/finance?tab=inbox",
  view_food_cost:   "/app/finance?tab=forecast",
};

// ── Action labels (Spanish, compact) ───────────────────────────────────
const ACTION_LABEL = {
  open_cash:        "Abrir caja",
  close_cash:       "Arquear",
  view_cash:        "Ver caja",
  close_sales_day:  "Cerrar ventas",
  view_sales:       "Ver ventas",
  close_inventory:  "Hacer inventario",
  view_inventory:   "Ver inventario",
  close_production: "Cerrar producción",
  view_production:  "Ver producción",
  review_inbox:     "Revisar bandeja",
  view_inbox:       "Ver bandeja",
  view_food_cost:   "Ver tendencia",
};

export default function DailyClosingStatusWidget({ restaurantId }) {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  // Note: dismiss localStorage removed (Feb 2026). Previously the X button
  // hid the panel for the rest of the day, but users kept "losing" the
  // widget after a single click. Now the user can hide it permanently
  // from the "Editar panel" popover; this widget either shows when
  // relevant or simply isn't rendered if data is empty.

  const load = useCallback(async () => {
    if (!restaurantId) return;
    setLoading(true);
    try {
      const r = await api.getTodayClosingStatus(restaurantId);
      setData(r);
    } catch (e) {
      console.warn("today-status", e);
    } finally {
      setLoading(false);
    }
  }, [restaurantId]);

  useEffect(() => { load(); }, [load]);

  // Reserve slot while loading so the bento grid doesn't reflow.
  if (loading || !data) return <BentoSlotSkeleton height={56} />;

  // ── Gating: dormant version when not yet active ───────────────────────
  // The panel only "wakes up" when the restaurant is open today, the cash
  // session is open, there are TPV sales and we're within the last hour
  // of any configured shift. Otherwise we render a slim explanatory card
  // so the user knows where the panel went.
  if (data.gating && data.gating.active === false) {
    return (
      <Card data-testid="daily-closing-status-dormant" className="border-border bg-muted/30">
        <CardContent className="p-3 flex items-center gap-3">
          <div className="h-9 w-9 rounded-full bg-muted text-muted-foreground flex items-center justify-center flex-shrink-0">
            <Moon className="h-4 w-4" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="font-semibold text-foreground text-sm">
              Panel de cierre en reposo
            </div>
            <div className="text-[11px] text-muted-foreground leading-snug">
              {data.gating.reason || "El panel se activará cuando empieces a recibir ventas y estemos en la última hora del turno."}
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  // Self-show when everything is OK (no noise when the day is fully closed)
  if (data.all_closed) {
    return (
      <Card data-testid="daily-closing-status-widget" className="border-emerald-200 bg-emerald-50/40">
        <CardContent className="p-3 flex items-center gap-3">
          <div className="h-9 w-9 rounded-full bg-emerald-500 text-white flex items-center justify-center">
            <CheckCircle2 className="h-5 w-5" />
          </div>
          <div className="flex-1">
            <div className="font-semibold text-emerald-900 text-sm">
              Todos los cierres de hoy están al día
            </div>
            <div className="text-[11px] text-emerald-700">
              Caja, ventas, inventario, producción y gastos · {data.day}
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  const { done, total, pending, warnings } = data.progress || {};
  const progressPct = total ? Math.round((done / total) * 100) : 0;

  return (
    <Card data-testid="daily-closing-status-widget" className="border-violet-200 overflow-hidden">
      <div className="bg-gradient-to-r from-violet-50 via-fuchsia-50 to-amber-50 px-4 py-2.5 flex items-center justify-between border-b border-violet-100">
        <div className="flex items-center gap-3">
          <div className="relative h-10 w-10">
            <svg className="h-10 w-10 -rotate-90" viewBox="0 0 36 36">
              <circle cx="18" cy="18" r="15" fill="none" stroke="#e9d5ff" strokeWidth="3" />
              <circle
                cx="18" cy="18" r="15" fill="none"
                stroke="#8b5cf6" strokeWidth="3"
                strokeDasharray={`${progressPct * 0.94} 100`}
                strokeLinecap="round"
              />
            </svg>
            <div className="absolute inset-0 flex items-center justify-center text-[11px] font-bold text-violet-700">
              {done}/{total}
            </div>
          </div>
          <div>
            <div className="font-semibold text-sm text-violet-900 flex items-center gap-2">
              Estado del cierre de hoy
              <span className="text-[10px] text-violet-600 font-normal">{data.day}</span>
            </div>
            <div className="text-[11px] text-violet-700 flex flex-wrap items-center gap-2">
              {done > 0 && <span>{done} cerrad{done === 1 ? "o" : "os"}</span>}
              {pending > 0 && <span className="text-orange-700">· {pending} pendiente{pending > 1 ? "s" : ""}</span>}
              {warnings > 0 && <span className="text-amber-700">· {warnings} con aviso{warnings > 1 ? "s" : ""}</span>}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-1">
          <Button
            size="sm" variant="ghost" onClick={load}
            className="text-violet-700 hover:bg-violet-100 h-7 w-7 p-0"
            title="Actualizar"
            data-testid="daily-closing-status-refresh"
          >
            <RefreshCw className="h-3.5 w-3.5" />
          </Button>
        </div>
      </div>

      <CardContent className="p-0">
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 divide-x-0 md:divide-x md:divide-y-0">
          {(data.items || []).map(item => {
            const tone = STATUS_TONE[item.status] || STATUS_TONE.none;
            const Icon = ITEM_ICON[item.id] || CircleDashed;
            const StatusIcon = tone.Icon;
            const route = ACTION_ROUTES[item.action] || "/app/finance?tab=closings";
            const actionLabel = ACTION_LABEL[item.action] || "Ver";
            const isActionable = ["pending", "missing", "warning"].includes(item.status);
            const foodCostValue = item.id === "food_cost" && item.value_pct !== null && item.value_pct !== undefined
              ? `${item.value_pct.toFixed(1)}%` : null;
            return (
              <div
                key={item.id}
                className={`p-3 flex flex-col ${tone.bg} transition-colors hover:brightness-[0.98] cursor-pointer border-t first:border-t-0 md:border-t-0`}
                onClick={() => navigate(route)}
                data-testid={`daily-closing-item-${item.id}`}
              >
                <div className="flex items-start gap-2 mb-1.5">
                  <div className={`h-7 w-7 rounded-md ${tone.bd} border bg-card flex items-center justify-center shrink-0`}>
                    <Icon className={`h-3.5 w-3.5 ${tone.fg}`} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-[11px] font-semibold text-foreground flex items-center gap-1">
                      {item.label}
                      {foodCostValue && (
                        <span className={`ml-auto text-[13px] font-bold ${tone.fg} tabular-nums`}>
                          {foodCostValue}
                        </span>
                      )}
                    </div>
                    <div className={`inline-flex items-center gap-1 text-[10px] font-medium ${tone.fg} mt-0.5`}>
                      <StatusIcon className="h-2.5 w-2.5" />
                      {item.status === "ok" ? "Cerrado"
                        : item.status === "warning" ? "Revisar"
                        : item.status === "pending" ? "Pendiente"
                        : item.status === "missing" ? "Falta"
                        : "Sin actividad"}
                    </div>
                  </div>
                </div>
                <p className="text-[10px] text-muted-foreground mb-2 min-h-[24px] line-clamp-2" title={item.detail}>
                  {item.detail}
                </p>
                {isActionable ? (
                  <Button
                    size="sm" variant="outline"
                    className={`w-full h-7 text-[10px] gap-1 ${tone.fg} border ${tone.bd} hover:brightness-95`}
                    onClick={(e) => { e.stopPropagation(); navigate(route); }}
                    data-testid={`daily-closing-cta-${item.id}`}
                  >
                    {actionLabel} <ArrowRight className="h-2.5 w-2.5" />
                  </Button>
                ) : (
                  <div className="w-full text-[10px] text-muted-foreground text-center py-1">
                    {actionLabel} →
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
}
